// OP-3619 — Código de documento → tipo, rótulo e rota, num lugar só.
//
// O razão grava a remessa como `referencia_tipo = 'SOLICITACAO'` com código
// `REM-`. Quem resolve isso é o servidor, em `referenciaDocumento`, com as
// mesmas condições que resolvem o id — e o que ele diz vale, inclusive o
// "não é documento" de um texto avulso que começa com "REM-". Sem o campo, a
// tela aplica a MESMA regra do servidor: tipo gravado e prefixo precisam bater.
// Quem já sabe o documento (a faixa da vida) passa o tipo dele direto.
import { DocumentoReferencia } from '../../../types/estoque'

export type TipoDocumento = DocumentoReferencia

interface Documento {
  prefixo: string
  /** O `referencia_tipo` com que o razão grava este documento. */
  gravado: string
  tipo: TipoDocumento
  rotulo: string
  rota: string
}

const DOCUMENTOS: Documento[] = [
  { prefixo: 'REQ-', gravado: 'SOLICITACAO', tipo: 'PEDIDO', rotulo: 'Pedido', rota: '/interno/estoque/fila' },
  { prefixo: 'REM-', gravado: 'SOLICITACAO', tipo: 'REMESSA', rotulo: 'Remessa', rota: '/interno/estoque/remessas' },
  { prefixo: 'TRF-', gravado: 'TRANSFERENCIA', tipo: 'TRANSFERENCIA', rotulo: 'Transferência', rota: '/interno/estoque/transferencias' },
  { prefixo: 'VIN-', gravado: 'CONTRATO', tipo: 'CONTRATO', rotulo: 'Contrato', rota: '/interno/estoque/vinculos' },
  { prefixo: 'INV-', gravado: 'INVENTARIO', tipo: 'CONTAGEM', rotulo: 'Contagem', rota: '/interno/estoque/inventarios' },
]

const porTipo = (tipo: TipoDocumento | null): Documento | null => DOCUMENTOS.find((d) => d.tipo === tipo) ?? null

export function documentoDoCodigo(codigo: string | null | undefined): Documento | null {
  const c = String(codigo || '').toUpperCase()
  return DOCUMENTOS.find((d) => c.indexOf(d.prefixo) === 0) ?? null
}

/**
 * `documento` é o campo do servidor: `undefined` = não veio; `null` = o
 * servidor disse que não é documento, e isso vale.
 */
export function documentoDaReferencia(
  tipo: string | null | undefined,
  codigo: string | null | undefined,
  documento?: TipoDocumento | null,
): TipoDocumento | null {
  if (documento !== undefined) return documento
  const t = String(tipo || '').toUpperCase()
  const jaSabido = porTipo(t as TipoDocumento)
  if (jaSabido) return jaSabido.tipo
  const doCodigo = documentoDoCodigo(codigo)
  return doCodigo && doCodigo.gravado === t ? doCodigo.tipo : null
}

export const rotuloDoDocumento = (tipo: TipoDocumento): string => porTipo(tipo)?.rotulo ?? tipo

export function baseDaReferencia(tipo: string | null | undefined, codigo: string | null | undefined, documento?: TipoDocumento | null): string | null {
  return porTipo(documentoDaReferencia(tipo, codigo, documento))?.rota ?? null
}

export function rotaDaReferencia(
  tipo: string | null | undefined,
  id: number | null | undefined,
  codigo?: string | null,
  documento?: TipoDocumento | null,
): string | null {
  const base = baseDaReferencia(tipo, codigo, documento)
  return base && id ? `${base}/${id}` : null
}

const ROTULO_REFERENCIA: Record<string, string> = {
  AVULSO: 'Lançamento avulso', COMPRA: 'Compra', MANUTENCAO: 'Manutenção', MIGRACAO: 'Migração',
  INVENTARIO: 'Inventário', SOLICITACAO: 'Pedido', TRANSFERENCIA: 'Transferência', CONTRATO: 'Contrato',
}

/** "Remessa · REM-2026-0007" em vez de "SOLICITACAO · REM-2026-0007". */
export function textoDaReferencia(tipo: string | null | undefined, codigo: string | null | undefined, documento?: TipoDocumento | null): string {
  const doc = documentoDaReferencia(tipo, codigo, documento)
  const rotulo = doc ? rotuloDoDocumento(doc) : (tipo ? ROTULO_REFERENCIA[String(tipo).toUpperCase()] ?? tipo : tipo)
  return [rotulo, codigo].filter(Boolean).join(' · ')
}
